import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { EditLocationAltOutlined } from '@mui/icons-material';

import messages from '../../../assets/locale/messages';
import pagesTitles from '../../../assets/locale/En/pagesTitles';
import { setPageMainTitle } from '../../../utils/Helpers';
import { weekDays, weekDaysAr } from '../../../utils/Constants';
import { ROUTE_PATHS } from '../../../utils/RoutesPaths';
import EmptyState from '../../../components/EmptyState';
import { getCustomerDetailsRequest, getCustomerDetailsResponse } from '../../../store/Customers/actions';
import Modal from '../../../components/Modal';
import SelectLocation from '../../Branches/SelectLocation';
import './ViewCustomer.scss';

const ViewCustomersDetails = () => {
  const lang = useSelector((state) => state.locale.lang);
  const { branches, shared, customers } = messages[lang];
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();

  const customerDetails = useSelector((state) => state.customers.customerDetails);
  const [openMap, setOpenMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    setPageMainTitle('customersManagement');
    return () => {
      dispatch(
        getCustomerDetailsResponse({
          customerDetails: {},
        })
      );
    };
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getCustomerDetailsRequest({ id }));
    }
  }, [id]);

  const handleCloseMapModal = () => {
    setOpenMap(false);
    setSelectedLocation(null);
  };

  const renderBranchCustomerDetails = () => {
    return (
      <div className="customerDetails">
        <div className="d-flex mb-4 flex-column flex-md-row">
          <Typography variant="h4" sx={{ flexGrow: 1 }} className={`page-title ${lang === 'en' ? 'me-5' : 'ms-5'}`}>
            {customerDetails
              ? customerDetails?.first_name && customerDetails?.last_name
                ? customerDetails?.first_name + ' ' + customerDetails?.last_name
                : pagesTitles?.customerView
              : pagesTitles.customerView}
          </Typography>
        </div>
        {Object.keys(customerDetails).length > 0 ? (
          <>
            <div>
              {/* name and phone number */}
              <div className={`bg-white rounded-4 p-4 ${!customerDetails && 'd-flex justify-content-center'}`}>
                <div className="mb-4">
                  <div className="text-dark font-medium mb-2">{branches.viewBranch.branchReviews.customerName}</div>
                  <div className="text-gray font-medium">{customerDetails?.first_name || '-'} {customerDetails?.last_name || '-'}</div>
                </div>
                <div className="mb-4">
                  <div className="text-dark font-medium mb-2">{branches.addBranch.labels.phone}</div>
                  <div className="text-gray font-medium">{customerDetails?.phone_number || '-'}</div>
                </div>
                <div className="mb-4">
                  <div className="text-dark font-medium mb-2">{customers.viewCustomer.email}</div>
                  <div className="text-gray font-medium">{customerDetails?.email || '-'}</div>
                </div>
                <div className="mb-4">
                  <div className="text-dark font-medium mb-2">{customers.viewCustomer.gender}</div>
                  <div className="text-gray font-medium">{customers.viewCustomer[customerDetails?.gender] || '-'}</div>
                </div>
              </div>
              {/* address */}
              <div className="bg-white rounded-4 p-4 mt-4">
                <div className="mb-4">
                  <div className="text-dark font-medium mb-2">{customers.viewCustomer.addresses}</div>
                </div>
                {customerDetails?.addresses?.length > 0 ? (
                  customerDetails?.addresses?.map((address, index) => {
                    return (
                      <div key={address.id || index} className="mb-4 address">
                        <div className="d-flex justify-content-between align-items-start flex-wrap">
                          <div>
                            <div className="mb-2 d-flex handle-small-gap">
                              <div className="text-dark font-medium mb-2">{customers.viewCustomer.title}:</div>
                              <div className="text-gray font-medium">{address?.title || '-'}</div>
                            </div>
                            <div className="d-flex handle-small-gap flex-wrap">
                              <div className="text-dark font-medium mb-2">{customers.viewCustomer.address}:</div>
                              {address?.building_number && (
                                <div className="text-gray font-medium">{address?.building_number}</div>
                              )}
                              {address?.street_name && (
                                <div className="text-gray font-medium">{address?.street_name}</div>
                              )}
                              ,
                              {address?.secondary_phone && (
                                <div className="text-gray font-medium">{address?.secondary_phone}</div>
                              )}
                              {address?.region && <div className="text-gray font-medium">{address?.region}</div>}
                              {address?.city && <div className="text-gray font-medium">{address?.city}</div>}
                              {address?.country && <div className="text-gray font-medium">{address?.country}</div>}
                            </div>

                            <div className="mt-2 d-flex handle-small-gap">
                              <div className="text-dark font-medium mb-2">{customers.viewCustomer.landmark}:</div>
                              <div className="text-gray font-medium">{address?.landmark || '-'}</div>
                            </div>
                          </div>
                          <div
                            className="fsize-16 text-secondary d-flex gap-1 align-items-center pointer  text-underline"
                            onClick={() => {
                              setSelectedLocation(address);
                              setOpenMap(true);
                            }}>
                            <EditLocationAltOutlined />
                            <div>{branches.viewBranch.labels.viewOnMap}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-gray font-medium">{customers.viewCustomer.noAddress}</div>
                )}
              </div>
            </div>
            <Modal
              open={openMap}
              handleClose={handleCloseMapModal}
              title={<h2>{branches.viewBranch.labels.supportedRegions}</h2>}
              contentClass="mb-5 pb-2 text-primary"
              content={
                <SelectLocation
                  viewMapInModelOnly={true}
                  showCoords={
                    selectedLocation ? { coords: { lng: +selectedLocation?.lng, lat: +selectedLocation?.lat } } : null
                  }
                />
              }
              confirmBtnTxt={shared.confirm}
              isContentText={false}
              maxWidth="lg"
              cancelBtnTxt={shared.cancel}
              handleCancel={handleCloseMapModal}
              modalClass="map-modal"
              actionsClass="container px-4"
              confirmBtnClass="btn-secondary fsize-14 text-white py-2 "
              cancelBtnClass="fsize-14 py-2"
              hasActions={false}
              disableActionBtn={false}
            />
          </>
        ) : (
          <div className="d-flex justify-content-center px-2 px-md-5 py-5 bg-white rounded-4">
            <EmptyState
              subTitle={shared.noDataToShow}
              ButtonLabel={shared.back}
              handleBtnAction={() => {
                history.push(ROUTE_PATHS.customers);
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return <>{customerDetails && renderBranchCustomerDetails()}</>;
};

export default ViewCustomersDetails;
